import type { LocalizationDictionary } from "./LocalizationTypes";

/**
 * The shell's English, compiled in: the floor under every served dictionary and the text while none
 * has loaded. Its served copies ship from `packages/game-shell/assets-src/shell-ui/localization/shell.<locale>.json`
 * into every title's manifest and must carry exactly these keys, the English one word for word — the
 * shell's localization parity test holds them together (review finding 38; decision 40).
 */
export const shellEnDictionary: LocalizationDictionary = {
  "common.back":"Back",
  "common.close":"Close",
  "common.ok":"Ok",

  "common.buy":"Buy",
  "common.decline":"No thanks",
  // The overlays' "tap anywhere" hint (`ModalChrome`, the shell's since decision 46).
  "common.tap_anywhere":"Tap anywhere to continue",

  // Nothing shows these three yet: the spin and slam faces and the autospin stop are icons. They stay as
  // the shell's words for its own controls, for a label or an accessible name; the parity test lets
  // these alone go unread (review 2026-09-27 #105).
  "common.spin":"Spin",
  "common.stop":"Stop",
  "common.slam":"Slam",

  "input.hold_to_spin":"Hold for Turbo Spins",

  "common.loading":"Loading...",
  "common.balance":"Balance",
  "common.win":"Win",
  "common.bet":"Bet",

  "intro.prompt": "Tap to start",
  "intro.volatility": "VOLATILITY",

  "lowbalance.popup.title":"Insufficient Funds",
  "lowbalance.popup.message":"Please reduce the bet size or add funds to your account",
  "spin_in_flight.popup.title":"Spin in Progress",
  "spin_in_flight.popup.message":"Another spin on your account is still running, possibly in another window. This spin was not placed, and no bet was taken. Wait a moment, then spin again.",
  "limit_reached.popup.title":"Limit Reached",
  "limit_reached.popup.message":"You have reached a limit set on your account. This spin was not placed, and no bet was taken.",
  "previous_round_unsettled.popup.title":"Previous Round Being Settled",
  "previous_round_unsettled.popup.message":"Your previous round is still being settled. This spin was not placed, and no bet was taken. Please try again in a moment.",
  "spin_not_placed.popup.title":"Spin Not Placed",
  "spin_not_placed.popup.message":"The spin did not go through, and your bet will be returned to your balance. Please try again in a moment.",
  "rate_limited.popup.title":"Please Wait",
  "rate_limited.popup.message":"Too many requests came in a short time. This spin was not placed, and no bet was taken. Wait a moment, then spin again.",
  "launch_refused.title":"Game Link Not Valid",
  "launch_refused.message":"This game link has expired or isn't valid. Return to the casino and open the game again.",

  "connectivity.network.title":"Connection Lost",
  "connectivity.network.message":"The game cannot reach the server. Check your internet connection, then reload the game. Any round in progress is saved on the server.",
  "connectivity.server.title":"Server Unavailable",
  "connectivity.server.message":"The game server is temporarily unavailable. Wait a moment, then reload the game. Any round in progress is saved on the server.",
  "connectivity.protocol.title":"Unexpected Server Response",
  "connectivity.protocol.message":"The game received a response it could not read. Please reload the game. Any round in progress is saved on the server. If this keeps happening, contact support.",
  "connectivity.session.title":"Session Ended",
  "connectivity.session.message":"Your session has ended. Reload the game or open it again from the casino lobby. Any round in progress is saved on the server.",
  "connectivity.session_superseded.message":"This game was opened in another window or on another device, so this session has ended. Reload to play here. Any round in progress is saved on the server.",
  "replay.failed.title":"Replay Failed",
  "replay.failed.message":"This round could not be replayed. Reload to try again.",
  "common.reload":"Reload",
  "system.error.generic.title":"Something went wrong",
  "system.error.generic.message":"Please reload the game. Any round in progress is saved on the server. If this keeps happening, contact support.",

  "menu.sound": "All Sounds",
  "menu.music": "Music",
  "menu.vibration": "Vibration",
  "menu.info": "Info",
  "menu.home": "Home",

  "speed_mode.title": "Speed Modes",
  "speed_mode.turbo": "Fast",
  "speed_mode.super_turbo": "Super Fast",

  "feature_purchase.title": "Feature Purchase",
  "feature_purchase.buy_bonus": "Buy Bonus",
  "feature_purchase.price": "Price",
  "feature_purchase.activate": "Activate",
  "feature_purchase.purchase": "Purchase",

  "feature_mode.disable": "Disable",

  // The bar's gamble entry (`ControlBarElement`); the gamble panel's own words are the title's (review 2026-10-01 #19).
  "gamble.title": "Gamble",

  "feature_hud.total_win": "Total win",
  "feature_hud.spins_left": "Spins left",

  // `FeatureFinishedPresenter` formats the extra-spins offer's cost; the panel's other words are the title's (#19).
  "featurefinished.offer.cost": "{count} spins cost {cost}",

  "autospin.title": "Autospins",
  "autospin.start": "Start Autospins",
  "autospin.autospins_left": "Autospins left",

  "menu.history": "Game History",
  "history.title": "Game History",
  "history.tab.bet_history": "Bet History",
  "history.tab.biggest_wins_current": "Big Wins Current Game",
  "history.empty": "No data available",
  "history.column.date": "Date",
  "history.column.bet": "Bet",
  "history.column.win": "Win",
  "history.click_to_replay": "Click to Replay",
  /** A round the backend reports `failed` (closed before its result, so no replay). Not "Refunded" or "Voided": the backend does not yet refund a bet it debited (review 2026-09-27 #7). */
  "history.failed": "Failed",
  "history.load_more": "Load More",

  "restore.title": "Game Interrupted",
  "restore.message": "Your previous game was interrupted. Its win is credited to your balance either way: Continue replays the round first, No thanks skips the replay.",
  "restore.collect.message": "Your previous game was interrupted and cannot be shown right now. Its win is credited to your balance when you continue.",
  "restore.collect.message_amount": "Your previous game was interrupted and cannot be shown right now. Its win of {amount} is credited to your balance when you continue.",
  "restore.credited.title": "Win Credited",
  "restore.credited.message": "Your win of {amount} has been credited to your balance.",
  "restore.continue": "Continue",
  "restore.decline": "No thanks",
  "restore.error.title": "Restore Failed",
  "restore.error.message": "Your previous game could not be completed. It is still saved on the server. Reload to try again, and contact support if this keeps happening.",

  // ── Info page ────────────────────────────────────────────
  // The info page's shared sections: the shell's behaviours and the generic headings, read through a
  // title's template `{info.<path>}` tokens. A heading only one title's template needs is that title's
  // (review 2026-10-01 #18); a title's bodies (RTP, paytable, features) live in its `game.<locale>`.

  "info.title": "GAME INFO",

  /** Shown in place of the page body when neither the operator's CDN nor the bundled template served; reopening the page fetches again. */
  "info.unavailable": "Game information could not be loaded. Close this page and try again in a moment.",

  "info.about.title": "ABOUT THE GAME",

  "info.bet.title": "BET AND PLAY",
  "info.bet.body": "Minimum bet: {min}. Maximum bet: {max}.\nAvailable bets: {steps}.\nBet amounts can be adjusted using the bet selector.",

  "info.howToPlay.title": "HOW TO PLAY",
  "info.howToPlay.intro": "Use the controls below to play and customise your experience.",
  "info.howToPlay.spin.title": "SPIN",
  "info.howToPlay.spin.body": "Tap the spin button to play a round at your current bet.",
  "info.howToPlay.bet.title": "BET",
  "info.howToPlay.bet.body": "Use the − and + buttons to lower or raise your stake. Min: {min}. Max: {max}.",
  "info.howToPlay.autospin.title": "AUTOSPIN",
  "info.howToPlay.autospin.body": "Tap the autospin button to play multiple rounds automatically at your current bet. See the AUTOSPIN section below for the available counts and stop conditions.",
  "info.howToPlay.balance.title": "BALANCE",
  "info.howToPlay.balance.body": "Your current balance is shown at the bottom of the screen, under Balance. Balance, bets, and winnings are displayed in your account's currency.",
  "info.howToPlay.winMeter.title": "WIN",
  "info.howToPlay.winMeter.body": "The current round's win is shown at the bottom of the screen, under Win. After the round completes, the amount is added to your balance.",
  "info.howToPlay.menu.title": "MENU",
  "info.howToPlay.menu.body": "Open the menu to access sound and music settings, game history (where available), and other options.",

  "info.rtp.title": "RETURN TO PLAYER (RTP)",
  "info.volatility.title": "VOLATILITY",
  "info.maxWin.title": "MAXIMUM WIN",

  "info.howToWin.title": "HOW TO WIN",

  "info.paytable.title": "PAYTABLE",
  "info.paytable.body": "Win amounts shown reflect the currently selected bet.",

  "info.gameSpeed.title": "GAME SPEED",
  "info.gameSpeed.intro": "The speed selector adjusts how quickly animations play during a spin.",
  "info.gameSpeed.normal": "Normal — full animation timing.",
  "info.gameSpeed.fast": "Fast — animations play at an accelerated pace.",
  "info.gameSpeed.superFast": "Super Fast — animations play at the maximum supported pace.",
  "info.gameSpeed.skip": "You can also tap the grid during a spin to skip ahead to the round result. The round outcome is determined by the server and is not affected by the speed setting.",

  "info.autospin.title": "AUTOSPIN",
  "info.autospin.body1": "Autospin plays a chosen number of rounds automatically, one after another, at your current bet.",
  "info.autospin.body2": "Available counts: {presets}.",
  "info.autospin.stopHeader": "Autospin stops automatically when:",
  "info.autospin.stop.featureTriggered": "A feature is triggered.",
  "info.autospin.stop.balanceLow": "Your balance is not enough for the next spin.",
  "info.autospin.stop.userStop": "You tap the stop control.",
  "info.autospin.body3": "You can also stop autospin at any time. The outcome of each round during autospin is determined by the server in the same way as a manually triggered spin.",

  "info.holdToSpin.title": "HOLD TO SPIN",
  "info.holdToSpin.body1": "You can press and hold the spin button to play rounds continuously.",
  "info.holdToSpin.body2": "Spins continue while the spin button is held and stop when you release it, when a feature is triggered, or when your balance is not enough for the next spin.",
  "info.holdToSpin.body3": "The outcome of each round is determined by the server in the same way as a manually triggered spin.",

  "info.featurePurchase.title": "FEATURE PURCHASE",

  "info.disconnection.title": "DISCONNECTION",
  "info.disconnection.body1": "If a connection is lost during a round, the round outcome is preserved on the server and is not affected by the disconnection.",
  "info.disconnection.body2": "When the player reconnects, winning rounds offer a choice between replaying the round or having the win credited directly to the account. Non-winning rounds settle automatically without a replay.",

  "info.malfunction.title": "MALFUNCTION",
  "info.malfunction.body": "Malfunction voids all pays and plays.",

  "info.version.title": "VERSION",
  "info.version.body": "Game version: {version}",
};